export const riskConfig = {
  CRITICAL: {
    badge: 'bg-risk-critical-bg text-risk-critical-fg border border-risk-critical-border',
    dot: 'bg-risk-critical',
    bar: 'bg-risk-critical',
    label: 'Critical',
    token: 'critical',
  },
  HIGH: {
    badge: 'bg-risk-high-bg text-risk-high-fg border border-risk-high-border',
    dot: 'bg-risk-high',
    bar: 'bg-risk-high',
    label: 'High',
    token: 'high',
  },
  MED: {
    badge: 'bg-risk-medium-bg text-risk-medium-fg border border-risk-medium-border',
    dot: 'bg-risk-medium',
    bar: 'bg-risk-medium',
    label: 'Medium',
    token: 'medium',
  },
  LOW: {
    badge: 'bg-risk-low-bg text-risk-low-fg border border-risk-low-border',
    dot: 'bg-risk-low',
    bar: 'bg-risk-low',
    label: 'Low',
    token: 'low',
  },
};

/** Maps API / engine levels (low, moderate, …) and legacy badges (HIGH, MED, …) to riskConfig keys. */
export function normalizeRiskBadgeLevel(level) {
  if (level == null || level === '') return 'LOW';
  const s = String(level).toUpperCase();
  if (s === 'CRITICAL') return 'CRITICAL';
  if (s === 'HIGH') return 'HIGH';
  if (s === 'MED' || s === 'MEDIUM' || s === 'MODERATE') return 'MED';
  if (s === 'LOW') return 'LOW';
  return 'LOW';
}

/**
 * Badge that shows category + (optional) numeric score.
 * Variants:
 *   - 'default' (label only, compact)
 *   - 'withScore' (e.g. "High · 72")
 */
export function RiskBadge({ level, score, variant = 'default' }) {
  const key = normalizeRiskBadgeLevel(level);
  const cfg = riskConfig[key] || riskConfig.LOW;
  const hasScore = variant === 'withScore' && Number.isFinite(Number(score));
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide ${cfg.badge}`}
      title={hasScore ? `Risk: ${cfg.label} · Score ${Math.round(Number(score))}` : `Risk: ${cfg.label}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      <span>{cfg.label}</span>
      {hasScore && (
        <>
          <span className="opacity-40">·</span>
          <span className="app-numeric">{Math.round(Number(score))}</span>
        </>
      )}
    </span>
  );
}

export function Avatar({ name, size = 'md' }) {
  const initials = name
    ? name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  const sizes = { sm: 'w-7 h-7 text-xs', md: 'w-9 h-9 text-sm', lg: 'w-11 h-11 text-base' };
  return (
    <div
      className={`${sizes[size]} rounded-full flex items-center justify-center font-semibold shrink-0`}
      style={{
        background: 'var(--bg-s2)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-divider)',
      }}
    >
      {initials}
    </div>
  );
}

export function formatSalary(val) {
  const n = Number(val);
  if (val == null || !Number.isFinite(n)) return '—';
  return '$' + (n / 1000).toFixed(0) + 'K';
}

export function formatCurrency(val) {
  if (val == null || !Number.isFinite(Number(val))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(val));
}

export function PageHeader({ title, subtitle, action, meta }) {
  return (
    <div className="app-page-header">
      <div className="app-page-header__main">
        <h1 className="app-page-header__title">{title}</h1>
        {subtitle && <p className="app-page-header__subtitle">{subtitle}</p>}
      </div>
      {(meta || action) && (
        <div className="app-page-header__aside">
          {meta && <div className="app-page-header__meta">{meta}</div>}
          {action && <div className="app-page-header__action">{action}</div>}
        </div>
      )}
    </div>
  );
}
